import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export type ComboType = 'pizza' | 'rodizio' | 'normal';
export type PizzaPricingRule = 'highest' | 'average';

export interface Combo {
  id: string;
  restaurant_id: string;
  name: string;
  description: string | null;
  combo_type: ComboType;
  price: number;
  category_id: string | null;
  image_url: string | null;
  pizza_max_flavors: number | null;
  pizza_pricing_rule: PizzaPricingRule | null;
  rodizio_category_id: string | null;
  available_pdv: boolean;
  available_table: boolean;
  available_tablet: boolean;
  available_delivery: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category?: { name: string } | null;
  rodizio_category?: { name: string } | null;
}

export interface ComboItem {
  id: string;
  combo_id: string;
  product_id: string | null;
  item_type: 'fixed' | 'choice';
  choice_label: string | null;
  quantity: number;
  is_required: boolean;
  display_order: number;
  product?: { name: string; price: number } | null;
}

export interface ComboItemOption {
  id: string;
  combo_item_id: string;
  product_id: string;
  display_order: number;
  product?: { name: string; price: number } | null;
}

export interface ComboPizzaFlavor {
  id: string;
  combo_id: string;
  product_id: string;
  display_order: number;
  product?: { id?: string; name: string; price: number; image_url?: string | null } | null;
}

// Internal combo with details - for PDV use (uses legacy combo_items/combo_pizza_flavors tables)
export interface InternalComboWithDetails extends Combo {
  pizzaFlavors?: ComboPizzaFlavor[];
  items?: (ComboItem & { options?: ComboItemOption[] })[];
}

export interface ComboFormData {
  name: string;
  description?: string;
  combo_type: ComboType;
  price: number;
  category_id?: string;
  image_url?: string;
  pizza_max_flavors?: number;
  pizza_pricing_rule?: PizzaPricingRule;
  rodizio_category_id?: string;
  available_pdv?: boolean;
  available_table?: boolean;
  available_tablet?: boolean;
  available_delivery?: boolean;
  is_active?: boolean;
  // For normal combos
  items?: {
    product_id?: string;
    item_type: 'fixed' | 'choice';
    choice_label?: string;
    quantity?: number;
    is_required?: boolean;
    options?: string[]; // product_ids for choice items
  }[];
  // For pizza combos
  pizza_flavors?: string[]; // product_ids
}

export function useCombos() {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();
  const restaurantId = activeRestaurant?.id;

  const { data: combos = [], isLoading, error } = useQuery({
    queryKey: ['combos', restaurantId],
    queryFn: async () => {
      if (!restaurantId) return [];
      
      const { data, error } = await supabase
        .from('combos')
        .select(`
          *,
          category:categories!combos_category_id_fkey(name),
          rodizio_category:categories!combos_rodizio_category_id_fkey(name)
        `)
        .eq('restaurant_id', restaurantId)
        .order('name');
      
      if (error) throw error;
      return data as Combo[];
    },
    enabled: !!restaurantId,
  });

  const createCombo = useMutation({
    mutationFn: async (formData: ComboFormData) => {
      if (!restaurantId) throw new Error('Restaurant not found');

      // Create the combo
      const { data: combo, error: comboError } = await supabase
        .from('combos')
        .insert({
          restaurant_id: restaurantId,
          name: formData.name,
          description: formData.description || null,
          combo_type: formData.combo_type,
          price: formData.price,
          category_id: formData.category_id || null,
          image_url: formData.image_url || null,
          pizza_max_flavors: formData.pizza_max_flavors || null,
          pizza_pricing_rule: formData.pizza_pricing_rule || null,
          rodizio_category_id: formData.rodizio_category_id || null,
          available_pdv: formData.available_pdv ?? true,
          available_table: formData.available_table ?? true,
          available_tablet: formData.available_tablet ?? true,
          available_delivery: formData.available_delivery ?? true,
          is_active: formData.is_active ?? true,
        })
        .select()
        .single();

      if (comboError) throw comboError;

      // For pizza combos, insert flavors
      if (formData.combo_type === 'pizza' && formData.pizza_flavors?.length) {
        const flavorsToInsert = formData.pizza_flavors.map((productId, index) => ({
          combo_id: combo.id,
          product_id: productId,
          display_order: index,
        }));

        const { error: flavorsError } = await supabase
          .from('combo_pizza_flavors')
          .insert(flavorsToInsert);

        if (flavorsError) throw flavorsError;
      }

      // For normal combos, insert items
      if (formData.combo_type === 'normal' && formData.items?.length) {
        for (let i = 0; i < formData.items.length; i++) {
          const item = formData.items[i];
          
          const { data: comboItem, error: itemError } = await supabase
            .from('combo_items')
            .insert({
              combo_id: combo.id,
              product_id: item.item_type === 'fixed' ? item.product_id : null,
              item_type: item.item_type,
              choice_label: item.choice_label || null,
              quantity: item.quantity || 1,
              is_required: item.is_required ?? true,
              display_order: i,
            })
            .select()
            .single();

          if (itemError) throw itemError;

          // If it's a choice item, insert options
          if (item.item_type === 'choice' && item.options?.length) {
            const optionsToInsert = item.options.map((productId, index) => ({
              combo_item_id: comboItem.id,
              product_id: productId,
              display_order: index,
            }));

            const { error: optionsError } = await supabase
              .from('combo_item_options')
              .insert(optionsToInsert);

            if (optionsError) throw optionsError;
          }
        }
      }

      return combo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos', restaurantId] });
      toast.success('Combo criado com sucesso');
    },
    onError: (error) => {
      console.error('Error creating combo:', error);
      toast.error('Erro ao criar combo');
    },
  });

  const updateCombo = useMutation({
    mutationFn: async ({ id, ...formData }: ComboFormData & { id: string }) => {
      if (!restaurantId) throw new Error('Restaurant not found');

      // Update combo basic data
      const { error: comboError } = await supabase
        .from('combos')
        .update({
          name: formData.name,
          description: formData.description || null,
          price: formData.price,
          category_id: formData.category_id || null,
          image_url: formData.image_url || null,
          pizza_max_flavors: formData.pizza_max_flavors || null,
          pizza_pricing_rule: formData.pizza_pricing_rule || null,
          rodizio_category_id: formData.rodizio_category_id || null,
          available_pdv: formData.available_pdv ?? true,
          available_table: formData.available_table ?? true,
          available_tablet: formData.available_tablet ?? true,
          available_delivery: formData.available_delivery ?? true,
          is_active: formData.is_active ?? true,
        })
        .eq('id', id);

      if (comboError) throw comboError;

      // For pizza combos, update flavors
      if (formData.combo_type === 'pizza' && formData.pizza_flavors) {
        // Delete existing flavors
        await supabase
          .from('combo_pizza_flavors')
          .delete()
          .eq('combo_id', id);

        // Insert new flavors
        if (formData.pizza_flavors.length > 0) {
          const flavorsToInsert = formData.pizza_flavors.map((productId, index) => ({
            combo_id: id,
            product_id: productId,
            display_order: index,
          }));

          const { error: flavorsError } = await supabase
            .from('combo_pizza_flavors')
            .insert(flavorsToInsert);

          if (flavorsError) throw flavorsError;
        }
      }

      // For normal combos, update items
      if (formData.combo_type === 'normal' && formData.items) {
        // Delete existing items (cascade will delete options)
        await supabase
          .from('combo_items')
          .delete()
          .eq('combo_id', id);

        // Insert new items
        for (let i = 0; i < formData.items.length; i++) {
          const item = formData.items[i];
          
          const { data: comboItem, error: itemError } = await supabase
            .from('combo_items')
            .insert({
              combo_id: id,
              product_id: item.item_type === 'fixed' ? item.product_id : null,
              item_type: item.item_type,
              choice_label: item.choice_label || null,
              quantity: item.quantity || 1,
              is_required: item.is_required ?? true,
              display_order: i,
            })
            .select()
            .single();

          if (itemError) throw itemError;

          if (item.item_type === 'choice' && item.options?.length) {
            const optionsToInsert = item.options.map((productId, index) => ({
              combo_item_id: comboItem.id,
              product_id: productId,
              display_order: index,
            }));

            const { error: optionsError } = await supabase
              .from('combo_item_options')
              .insert(optionsToInsert);

            if (optionsError) throw optionsError;
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos', restaurantId] });
      toast.success('Combo atualizado com sucesso');
    },
    onError: (error) => {
      console.error('Error updating combo:', error);
      toast.error('Erro ao atualizar combo');
    },
  });

  const deleteCombo = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('combos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos', restaurantId] });
      toast.success('Combo excluído com sucesso');
    },
    onError: (error) => {
      console.error('Error deleting combo:', error);
      toast.error('Erro ao excluir combo');
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('combos')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos', restaurantId] });
    },
    onError: (error) => {
      console.error('Error toggling combo:', error);
      toast.error('Erro ao alterar status do combo');
    },
  });

  return {
    combos,
    isLoading,
    error,
    createCombo,
    updateCombo,
    deleteCombo,
    toggleActive,
  };
}

// Hook to fetch combo details with items/flavors
export function useComboDetails(comboId: string | null) {
  return useQuery({
    queryKey: ['combo-details', comboId],
    queryFn: async () => {
      if (!comboId) return null;

      // Fetch combo
      const { data: combo, error: comboError } = await supabase
        .from('combos')
        .select(`
          *,
          category:categories!combos_category_id_fkey(name),
          rodizio_category:categories!combos_rodizio_category_id_fkey(name)
        `)
        .eq('id', comboId)
        .single();

      if (comboError) throw comboError;

      // Fetch pizza flavors if pizza type
      let pizzaFlavors: ComboPizzaFlavor[] = [];
      if (combo.combo_type === 'pizza') {
        const { data, error } = await supabase
          .from('combo_pizza_flavors')
          .select(`
            *,
            product:products(name, price)
          `)
          .eq('combo_id', comboId)
          .order('display_order');

        if (!error && data) {
          pizzaFlavors = data as ComboPizzaFlavor[];
        }
      }

      // Fetch items if normal type
      let items: (ComboItem & { options?: ComboItemOption[] })[] = [];
      if (combo.combo_type === 'normal') {
        const { data: itemsData, error: itemsError } = await supabase
          .from('combo_items')
          .select(`
            *,
            product:products(name, price)
          `)
          .eq('combo_id', comboId)
          .order('display_order');

        if (!itemsError && itemsData) {
          // Fetch options for each choice item
          for (const item of itemsData) {
            if (item.item_type === 'choice') {
              const { data: optionsData } = await supabase
                .from('combo_item_options')
                .select(`
                  *,
                  product:products(name, price)
                `)
                .eq('combo_item_id', item.id)
                .order('display_order');

              (item as any).options = optionsData || [];
            }
          }
          items = itemsData as (ComboItem & { options?: ComboItemOption[] })[];
        }
      }

      return {
        ...combo,
        pizzaFlavors,
        items,
      } as Combo & { pizzaFlavors: ComboPizzaFlavor[]; items: (ComboItem & { options?: ComboItemOption[] })[] };
    },
    enabled: !!comboId,
  });
}
